import React, { useState } from "react";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { BarChart3 } from "lucide-react";

import { CseTrackRecordTab } from "@/components/track-record/CseTrackRecordTab";
import { UsTrackRecordTab } from "@/components/track-record/UsTrackRecordTab";
import { CombinedTrackRecordSummary } from "@/components/track-record/CombinedTrackRecordSummary";

/**
 * TrackRecordPage — Phase 5 multi-market shell.
 *
 * Hosts three tabs (mandated by the user during Phase 5 design sync):
 *   • 🇱🇰 CSE                — original Phase 15 dashboard, vs ASPI, in LKR
 *   • 🇺🇸 US                 — Phase 5 dashboard, vs ^GSPC, in USD
 *   • Combined Summary       — read-only side-by-side KPI cards
 *
 * Strict separation: CSE and US data are NEVER merged or
 * currency-converted. Each tab pulls from its own backend service.
 *
 * The two market tabs lift their `summary` blocks up to this shell so
 * the Combined tab can render instantly without an extra round-trip
 * if the user has already visited either market tab.
 */
const TrackRecordPage = () => {
  const [tab, setTab] = useState("cse");
  const [cseSummary, setCseSummary] = useState(null);
  const [usSummary, setUsSummary] = useState(null);

  return (
    <div className="space-y-6" data-testid="track-record-page">
      {/* Page header */}
      <div className="space-y-1">
        <h1 className="font-display text-2xl md:text-3xl font-semibold flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-primary" />
          Track Record
        </h1>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Transparent performance tracking for every BUY / STRONG BUY signal we publish — across both
          markets, against their native benchmarks. CSE vs ASPI in LKR; US vs S&amp;P 500 in USD.
        </p>
      </div>

      {/* Market tabs */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList data-testid="track-record-market-tabs">
          <TabsTrigger value="cse" data-testid="track-record-market-cse">
            <span className="mr-1.5">🇱🇰</span> CSE
          </TabsTrigger>
          <TabsTrigger value="us" data-testid="track-record-market-us">
            <span className="mr-1.5">🇺🇸</span> US
          </TabsTrigger>
          <TabsTrigger value="combined" data-testid="track-record-market-combined">
            Combined Summary
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cse" className="m-0">
          <CseTrackRecordTab onSummaryLoaded={setCseSummary} />
        </TabsContent>

        <TabsContent value="us" className="m-0">
          <UsTrackRecordTab onSummaryLoaded={setUsSummary} />
        </TabsContent>

        <TabsContent value="combined" className="m-0">
          <CombinedTrackRecordSummary
            cseSummary={cseSummary}
            usSummary={usSummary}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TrackRecordPage;
